import { useRef, useState } from "react";

import Button from "../components/layout/Button";
import LoadingSpinner from "../components/layout/LoadingSpinner";
import FormError from "../components/salesDocuments/FormError";
import { ColumnPair, lookupBulkTps } from "../services/tps.service";

const MAX_PAIRS = 5;

const emptyPair = (): ColumnPair => ({ phoneColumn: "", tpsColumn: "" });

const BulkTpsLookup: React.FC = () => {
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [columnPairs, setColumnPairs] = useState<ColumnPair[]>([emptyPair()]);
  const [fileError, setFileError] = useState(false);
  const [pairErrors, setPairErrors] = useState<boolean[]>([false]);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setCsvFile(file);
    setFileError(false);
    setRequestError(null);
  };

  const updatePair = (index: number, field: keyof ColumnPair, value: string) => {
    setColumnPairs((prev) =>
      prev.map((pair, i) => (i === index ? { ...pair, [field]: value } : pair)),
    );
    setPairErrors((prev) => prev.map((err, i) => (i === index ? false : err)));
    setRequestError(null);
  };

  const addPair = () => {
    if (columnPairs.length < MAX_PAIRS) {
      setColumnPairs((prev) => [...prev, emptyPair()]);
      setPairErrors((prev) => [...prev, false]);
    }
  };

  const removePair = (index: number) => {
    setColumnPairs((prev) => prev.filter((_, i) => i !== index));
    setPairErrors((prev) => prev.filter((_, i) => i !== index));
  };

  const validate = (): boolean => {
    let valid = true;

    if (!csvFile) {
      setFileError(true);
      valid = false;
    }

    const newPairErrors = columnPairs.map((pair) => {
      const isEmpty = !pair.phoneColumn.trim() || !pair.tpsColumn.trim();
      if (isEmpty) valid = false;
      return isEmpty;
    });
    setPairErrors(newPairErrors);

    return valid;
  };

  const handleSubmit = async () => {
    setRequestError(null);

    if (!validate() || !csvFile) return;

    setIsSubmitting(true);
    try {
      const blob = await lookupBulkTps(csvFile, columnPairs);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "tps_bulk_results.csv";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch {
      setRequestError("Unable to complete bulk TPS lookup. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <h1>Check TPS Registration In Bulk</h1>

      <div className="meter-search-container">
        <div>
          <h4>Instructions for use</h4>
          This tool is allows you to submit a CSV file containing phone numbers and receive a new
          CSV with TPS lookup results. To use:
          <ol>
            <li>Prepare a CSV file with phone numbers and TPS values.</li>
            <li>Select the CSV file using the file input below.</li>
            <li>
              Map pairs of columns for phone numbers and TPS values.{" "}
              <i>Hint: Column names are case sensitive.</i>
            </li>
            <ul>
              <li>
                &apos;Phone number column&apos; must match to an existing column in your CSV file
                containing phone numbers.
              </li>
              <li>
                &apos;TPS value column&apos; should match to the desired column for lookup results.
              </li>
              <li>You can add up to {MAX_PAIRS} column pairs.</li>
              <li>
                If TPS columns do not exist, they will be created adjacent to the corresponding
                phone number columns.
              </li>
              <li>Pre-existing TPS columns will be written in-place.</li>
            </ul>
            <li>
              Click &apos;Submit&apos; to perform the lookup. A new CSV file will be downloaded with
              the results.
            </li>
            <li>
              For context, a file with around 25,000 rows will take approximately 25 seconds to
              process.
            </li>
          </ol>
        </div>

        <fieldset className="docs-form__section">
          <div className="docs-form__customer-field">
            <div className="docs-form__inputs">
              <label htmlFor="bulk-tps-csv">CSV File</label>
              <input
                id="bulk-tps-csv"
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileChange}
              />
            </div>
            {fileError && (
              <div className="docs-form__error-messages">
                <FormError text="Please select a CSV file." />
              </div>
            )}
          </div>

          <div className="docs-form__customer-field">
            <p>
              <strong>Column Mappings</strong>
            </p>

            {columnPairs.map((pair, index) => (
              <div key={index} className="docs-form__customer-field">
                <div style={{ display: "flex", gap: "1rem", alignItems: "flex-end" }}>
                  <div className="docs-form__inputs" style={{ flex: 1 }}>
                    <label htmlFor={`phone-col-${String(index)}`}>Phone number column</label>
                    <input
                      id={`phone-col-${String(index)}`}
                      type="text"
                      value={pair.phoneColumn}
                      onChange={(e) => {
                        updatePair(index, "phoneColumn", e.target.value);
                      }}
                      placeholder="e.g. phone_number"
                    />
                  </div>
                  <div className="docs-form__inputs" style={{ flex: 1 }}>
                    <label htmlFor={`tps-col-${String(index)}`}>TPS value column</label>
                    <input
                      id={`tps-col-${String(index)}`}
                      type="text"
                      value={pair.tpsColumn}
                      onChange={(e) => {
                        updatePair(index, "tpsColumn", e.target.value);
                      }}
                      placeholder="e.g. tps_value"
                    />
                  </div>
                  {columnPairs.length > 1 && (
                    <Button
                      type="button"
                      onClick={() => {
                        removePair(index);
                      }}
                    >
                      Remove
                    </Button>
                  )}
                </div>
                {pairErrors[index] && (
                  <div className="docs-form__error-messages">
                    <FormError text="Both column names are required." />
                  </div>
                )}
              </div>
            ))}

            {columnPairs.length < MAX_PAIRS && (
              <Button type="button" onClick={addPair}>
                + Add column pair
              </Button>
            )}
          </div>

          {requestError && (
            <div className="docs-form__error-messages">
              <FormError text={requestError} />
              <p style={{ marginTop: "8px" }}>
                <strong>Hint:</strong> Have you checked the column mappings? They are case
                sensitive.
              </p>
            </div>
          )}

          <div className="meter-details-footer">
            {isSubmitting ? (
              <LoadingSpinner message="Processing, this may take a moment..." size="button" />
            ) : (
              <Button type="button" onClick={handleSubmit} disabled={isSubmitting}>
                Submit
              </Button>
            )}
          </div>
        </fieldset>
      </div>
    </div>
  );
};

export default BulkTpsLookup;
